import { MouseEvent } from 'react';

import { IconLink } from '../../../icons';
import Button from '../../../components/Button/Button';
import RoutesEnum from '../../../core/enums/RoutesEnum';
import { copyTextToClipboard } from '../../../core/helpers';
import useNotification from '../../../core/hooks/useNotification';
import { AnalyticsAction } from '../../../core/models/AnalyticsAttributes';

import './CopySubArticleLinkButton.styles.scss';

interface iCopySubArticleLinkButtonProps {
    articleId: number;
    /** The sub-article paragraph's id attribute verbatim, e.g. `sub-3` */
    anchorId: string;
}

const CopySubArticleLinkButton = ({ articleId, anchorId }: iCopySubArticleLinkButtonProps) => {
    const { addNotification } = useNotification();

    const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        // Built from origin, not location.href, so `?loaded=true` and any current hash never leak into the link.
        // PUBLIC_URL is the router basename (empty today).
        const path = RoutesEnum.NewsArticle.replace(':id', String(articleId));
        const url = `${window.location.origin}${process.env.PUBLIC_URL}${path}#${anchorId}`;

        try {
            await copyTextToClipboard(url);
            addNotification({
                message: 'URL was copied to the clipboard',
                icon: <IconLink className="CopySubArticleLinkButton__toast-icon" aria-hidden="true" focusable="false" />
            });
        } catch {
            addNotification({
                status: 'error',
                message:
                    "Couldn't copy the link. Please try again, or open the sub-article and copy the URL from the address bar."
            });
        }
    };

    return (
        <Button
            unstyled
            className="CopySubArticleLinkButton"
            ariaLabel="Copy link to this sub-article"
            onClick={handleClick}
            analyticsData={{
                action: AnalyticsAction.NewsSubArticleCopyLink,
                reportId: articleId
            }}
        >
            <IconLink className="CopySubArticleLinkButton__icon" aria-hidden="true" focusable="false" />
        </Button>
    );
};

export default CopySubArticleLinkButton;
